import { GET_FACETS } from '@/graphql/queries';
import { districtVar, flyerboardAllFiltersVar } from '@/graphql/state';
import { GetFacetsResponse, OwningTypeEnum } from '@/graphql/types';
import { useQuery, useReactiveVar } from '@apollo/client';
import { css } from '@emotion/css';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { OptionType } from '@/components/FlyerboardFilters/FilterOption';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import JarFlyerboardContext from '@/context/JarFlyerboardContext';
import { useChildGrades, useCombinedCategories } from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import { communityCategories } from '@/mock/communityCategoriesMock';
import { schoolCategories } from '@/mock/schoolCategoriesMock';
import { getGradeRange } from '@/utils/getGradeRange';
import { isMobile } from '@/utils/isMobile';
import {
  COMBINED_CATEGORIES_CONFIG,
  COMBINED_ORGANIZATIONS_CATEGORIES,
  COMBINED_ORGANIZATIONS_ID,
  COMBINED_SPORTS_CATEGORIES,
  COMBINED_SPORTS_ID,
  COMBINED_VOLUNTEER_CATEGORIES,
  COMBINED_VOLUNTEER_ID,
  JAR_CATEGORIES_ORDER,
} from './jar.constants';
import { JarFilterDrawer } from './JarFilterDrawer';
import { JarFilterModal } from './JarFilterModal';

interface JarFilterProps {
  children: React.ReactNode;
}

type FiltersState = {
  cost: string[];
  provider: string[];
  types: string[];
};

const defaultFilters: FiltersState = {
  cost: [],
  provider: [],
  types: [],
};

export const JarFilter: React.FC<JarFilterProps> = ({ children }) => {
  const {
    setDrawerFilters,
    filters: jarFilters,
    drawerFilters,
    districtId,
  } = useContext(JarFlyerboardContext);
  const { search } = useLocation();
  const isMobileDevice = isMobile();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState<FiltersState>(defaultFilters);
  const [gradeFilter, setGradeFilter] = useState<{ from?: string; to?: string }>({});

  const { lowGrade, highGrade, grades } = useChildGrades();
  const district = useReactiveVar(districtVar)
  const flyerboardAllFilters = useReactiveVar(flyerboardAllFiltersVar);

  const { trackEvent } = useAnalytics();

  const trackingFrom = {
    event_from: TrackingEvents.viewFlyerSet,
    event_detail_from: 'jar',
    stage_from: TrackingStage.discovery,
  };

  useEffect(() => {
    if (!showFilter) return;

    setFilters(prev => {
      const newFilters: FiltersState = { ...prev };

      newFilters.cost = drawerFilters.isCommunityFree === true ? ['community_free'] : [];
      if (drawerFilters.owningType === OwningTypeEnum.district) {
        newFilters.provider = ['district'];
      } else if (drawerFilters.owningType === OwningTypeEnum.organization) {
        newFilters.provider = ['organization'];
      } else {
        newFilters.provider = [];
      }
      const typesFromParams = searchParams.get('types');
      if (typesFromParams) {
        newFilters.types = typesFromParams.split(',');
      }

      return newFilters;
    });

    const fromGrade = searchParams.get('fromGrade');
    const toGrade = searchParams.get('toGrade');

    if (fromGrade && toGrade) {
      setGradeFilter({
        from: fromGrade,
        to: toGrade,
      });
    } else {
      setDefaultGrades();
    }
  }, [showFilter, drawerFilters.isCommunityFree, drawerFilters.owningType, searchParams]);

  useEffect(() => {
    if (filters.cost.length > 0 && filters.provider.length > 0) {
      setFilters(prev => ({
        ...prev,
        provider: [],
      }));
    }
  }, [filters.cost]);

  const queryInput = useMemo(() => {
    const queryFilters = { ...jarFilters };

    const { types, cost, provider } = filters;
    let categories = [...types];

    if (types.includes(COMBINED_VOLUNTEER_ID)) {
      categories = [
        ...types.filter(t => t !== COMBINED_VOLUNTEER_ID),
        ...COMBINED_VOLUNTEER_CATEGORIES,
      ];
    }

    if (types.includes(COMBINED_SPORTS_ID)) {
      categories = [
        ...categories.filter(t => t !== COMBINED_SPORTS_ID),
        ...COMBINED_SPORTS_CATEGORIES,
      ];
    }

    if (types.includes(COMBINED_ORGANIZATIONS_ID)) {
      categories = [
        ...categories.filter(t => t !== COMBINED_ORGANIZATIONS_ID),
        ...COMBINED_ORGANIZATIONS_CATEGORIES,
      ];
    }

    queryFilters.categories = categories;

    if (provider.length > 0) {
      const owningType = OwningTypeEnum[provider[0]];
      queryFilters.owningType = owningType;
      if (owningType === OwningTypeEnum.organization) {
        queryFilters.schoolIds = [];
      }
    } else {
      queryFilters.owningType = undefined;
    }
    queryFilters.isCommunityFree = cost.length > 0;
    if (gradeFilter.from && gradeFilter.to) {
      const grades = getGradeRange(gradeFilter.from, gradeFilter.to).map(g => g.id.toString());
      queryFilters.grades = grades;
    }

    return {
      filters: {
        ...queryFilters,
        districtId: district?.districtId || Number(districtId),
        schoolIds: queryFilters.schoolIds ?? [],
      },
    };
  }, [flyerboardAllFilters, filters, gradeFilter]);

  const { data, previousData, loading } = useQuery<GetFacetsResponse>(GET_FACETS, {
    variables: {
      input: queryInput,
    },
    skip: flyerboardAllFilters === null,
  });

  const facets = data?.getFacets.facets ?? previousData?.getFacets.facets;
  const amount = data?.getFacets.pagination.total ?? previousData?.getFacets.pagination.total ?? 0;

  const { generateCombinedCategoryOptions } = useCombinedCategories({ facets });

  const viewResultsLabel = useMemo(() => `View ${amount} results`, [amount]);

  const setDefaultGrades = () => {
    setGradeFilter({
      from: lowGrade,
      to: highGrade,
    });
  };

  const generateProviderOptions = () => {
    const providerMap = {
      district: 'School',
      organization: 'Community',
    };

    const defaultProviderOptions = [
      { value: 'district', description: providerMap.district, amount: 0, disabled: false },
      { value: 'organization', description: providerMap.organization, amount: 0, disabled: false },
    ];

    if (!facets || !facets.owningType) return defaultProviderOptions;

    const owningTypeMap = Object.fromEntries(
      facets.owningType.map(({ key, count }) => [key, count])
    );

    const isCostSelected = filters.cost.length > 0;

    return defaultProviderOptions.map(opt => {
      const isChecked = filters.provider.includes(opt.value);
      let disabled = (owningTypeMap[opt.value] ?? 0) === 0;
      if (isCostSelected && !isChecked) {
        disabled = true;
      }
      return {
        ...opt,
        amount: owningTypeMap[opt.value] ?? 0,
        disabled,
      };
    });
  };

  const generateCostOption = () => {
    if (!facets) return [];
    return [
      {
        value: 'community_free',
        description: 'Free',
        amount,
        disabled: false,
      },
    ];
  };

  const generateTypesOptions = () => {
    if (!facets) return [];

    const categories = [...schoolCategories, ...communityCategories];
    const categoryOptions: Array<OptionType> = [];
    const addedIds = new Set();

    const combinedOptions = generateCombinedCategoryOptions;
    combinedOptions.forEach(option => {
      categoryOptions.push(option);
      const config = Object.values(COMBINED_CATEGORIES_CONFIG).find(
        c => c.label === option.description
      );
      if (config) {
        config.categories.forEach(cat => addedIds.add(cat));
      }
    });

    categories.forEach(category => {
      if (addedIds.has(category.id)) return;

      categoryOptions.push({
        description: category.title,
        value: category.id,
        disabled: false,
      });
      addedIds.add(category.id);
    });

    return categoryOptions.sort((a, b) => {
      const aIndex = JAR_CATEGORIES_ORDER.indexOf(a.value);
      const bIndex = JAR_CATEGORIES_ORDER.indexOf(b.value);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return 0;
    });
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setDefaultGrades();

    setDrawerFilters({
      owningType: undefined,
      isCommunityFree: undefined,
      grades: undefined,
      categories: undefined,
    });

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('filters');
    newSearchParams.delete('fromGrade');
    newSearchParams.delete('toGrade');
    newSearchParams.delete('types');
    newSearchParams.delete('quickFilter');
  };

  const viewResults = () => {
    const { owningType, categories, isCommunityFree, grades } = queryInput.filters;
    setDrawerFilters({
      categories,
      owningType,
      isCommunityFree,
      grades,
    });
    setShowFilter(false);

    trackEvent({
      event: TrackingEvents.filterFlyerSet,
      event_detail: EventDetails.filterPanel,
      stage: TrackingStage.discovery,
      ...trackingFrom,
    });
  };

  const handleToggleOption = (section: string, value: string) => {
    setFilters(prev => {
      const arr = prev[section] as string[];
      const newArr = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...prev, [section]: newArr };
    });
  };

  const handleGradeChange = (field: 'from' | 'to', value?: string) => {
    setGradeFilter(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const onToggleFilter = (state: boolean) => {
    setShowFilter(state);
  };

  const disableClearAllButton =
    Object.values(filters).every(arr => arr.length === 0) &&
    (!gradeFilter.from ||
      !gradeFilter.to ||
      (gradeFilter.from === lowGrade && gradeFilter.to === highGrade));

  // Shared props for both drawer and modal
  const filterProps = {
    isOpen: showFilter,
    onToggle: onToggleFilter,
    filters,
    setFilters,
    gradeFilter,
    setGradeFilter,
    loading,
    viewResultsLabel,
    resetFilters,
    viewResults,
    handleToggleOption,
    handleGradeChange,
    generateProviderOptions,
    generateCostOption,
    generateTypesOptions,
    disableClearAllButton,
    grades,
    lowGrade,
    highGrade,
    setDefaultGrades,
  };

  return (
    <>
      <button onClick={() => onToggleFilter(true)} className={cn.button}>
        {children}
      </button>
      {isMobileDevice ? <JarFilterDrawer {...filterProps} /> : <JarFilterModal {...filterProps} />}
    </>
  );
};

const cn = {
  button: css`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
  `,
};
